class OpticalPrescriptionPicker extends HTMLElement {
  connectedCallback() {
    this._infoContent = this.closest('.product__info-main_content');
    this._sectionId = this.dataset.section;
    this._data = {};

    // Trigger button
    const trigger =
      this._infoContent &&
      this._infoContent.querySelector('.select-lenses-trigger');
    if (trigger) trigger.addEventListener('click', () => this.open());

    // Close (screen 1)
    const closeBtn = this.querySelector('.optical-prescription-picker__close');
    if (closeBtn) closeBtn.addEventListener('click', () => this.close());

    // Back buttons
    this.querySelectorAll('.optical-prescription-picker__back').forEach(
      (btn) =>
        btn.addEventListener('click', () => {
          // Clear file state when going back from upload or manual screens
          if (
            btn.closest('[data-screen="upload"]') ||
            btn.closest('[data-screen="manual"]') ||
            btn.dataset.back === 'type'
          ) {
            this._clearFile();
          }
          this.showScreen(btn.dataset.back);
        })
    );

    // Screen 1: Prescription types
    this.querySelectorAll(
      '[data-screen="type"] .optical-prescription-picker__option'
    ).forEach((opt) =>
      opt.addEventListener('click', () => this.onTypeClick(opt))
    );

    // Screen 2: Method
    this.querySelectorAll(
      '[data-screen="method"] .optical-prescription-picker__option'
    ).forEach((opt) =>
      opt.addEventListener('click', () => this.onMethodClick(opt))
    );

    // File inputs (upload + manual screens)
    this.querySelectorAll('.optical-prescription-picker__file-input').forEach(
      (input) => input.addEventListener('change', (e) => this.onFileChange(e))
    );

    this._syncFileSuccessIcons();

    // PD radio inputs (all screens)
    this.querySelectorAll(
      'input[type="radio"][name^="optical-pd-type"]'
    ).forEach((r) =>
      r.addEventListener('change', () => this.onPdTypeChange(r))
    );

    // CYL selects — enable/disable AXIS
    this.querySelectorAll('[data-rx$="-cyl"]').forEach((cyl) => {
      const eye = cyl.dataset.rx.replace('-cyl', '');
      const axis = this.querySelector('[data-rx="' + eye + '-axis"]');
      if (axis) {
        cyl.addEventListener('change', () => {
          axis.disabled = !cyl.value;
          if (!cyl.value) axis.selectedIndex = 0;
        });
      }
    });

    // Save buttons (upload, manual, reading screens)
    this.querySelectorAll('.optical-prescription-picker__submit').forEach(
      (btn) =>
        btn.addEventListener('click', () => {
          const screen = btn.closest('.optical-prescription-picker__screen');
          if (screen.dataset.screen === 'upload') this.onSaveAndContinue();
          else if (screen.dataset.screen === 'manual') this.onManualSave();
          else if (screen.dataset.screen === 'reading') this.onReadingSave();
        })
    );

    // Screen 4: Lens cards
    this.querySelectorAll('.optical-prescription-picker__lens-card').forEach(
      (card) =>
        card.addEventListener('click', () => this.onLensClick(card))
    );

    // Screen 5: Sun lenses
    const addSun = this.querySelector('[data-action="add-sun-lenses"]');
    if (addSun)
      addSun.addEventListener('click', () => this.onSunLensesClick(true));

    const skipSun = this.querySelector('[data-action="skip-sun-lenses"]');
    if (skipSun)
      skipSun.addEventListener('click', () => this.onSunLensesClick(false));

    // Screen 6: Add to cart
    const addToCart = this.querySelector(
      '.optical-prescription-picker__add-to-cart'
    );
    if (addToCart)
      addToCart.addEventListener('click', () => this.onAddToCart());

    // Global close handler for modal close and overlay clicks (modal appended to body)
    this._globalClickHandler = (e) => {
      if (
        e.target.closest('[data-action="file-modal-close"]') ||
        e.target.closest('[data-action="file-modal-overlay"]')
      ) {
        this._closeFilePreview();
      }
    };
    document.addEventListener('click', this._globalClickHandler);
  }

  disconnectedCallback() {
    // Cleanup global listener and any preview URL when component is removed
    if (this._globalClickHandler) document.removeEventListener('click', this._globalClickHandler);
    this._closeFilePreview();
  }

  // ── Navigation ──

  open() {
    if (!this._infoContent) return;
    this.style.removeProperty('display');
    this._infoContent.classList.add('lens-picker-mode');
    this.showScreen('type');
  }

  close() {
    if (!this._infoContent) return;
    this._infoContent.classList.remove('lens-picker-mode');
    this._reset();
  }

  showScreen(name) {
    this.querySelectorAll('.optical-prescription-picker__screen').forEach(
      (s) => (s.hidden = s.dataset.screen !== name)
    );

    // Show the right lens groups container for the selected type
    if (name === 'lenses' && this._data.typeHandle) {
      this.querySelectorAll(
        '.optical-prescription-picker__lens-groups'
      ).forEach((g) => {
        g.hidden = g.dataset.typeHandle !== this._data.typeHandle;
      });
    }
  }

  // ── Screen 1: Type ──

  onTypeClick(opt) {
    this._highlightOption('[data-screen="type"]', opt);

    // If this option is frame-only, submit product form immediately
    if (opt.dataset.frameOnly === '0') {
      this._clickSubmitButton();
      return;
    }

    // Store selected type
    this._data.type = opt
      .querySelector('.optical-prescription-picker__option-title')
      .textContent.trim();
    this._data.typeHandle = opt.dataset.handle;
    this.querySelectorAll('[data-screen="method"] .optical-prescription-picker__option')
      .forEach((o) => o.classList.remove('optical-prescription-picker__option--selected'));
    // Clear previously chosen method data
    delete this._data.method;

    // Clear prescription-specific inputs/state when changing type to avoid stale data
    this._clearPrescriptionState();

    // If reading type, show dedicated reading screen
    if (this._data.typeHandle === 'reading') {
      this._setLensesBack('reading');
      this.showScreen('reading');
      return;
    }

    if (this._data.typeHandle === 'non-prescription') {
      this._setLensesBack('type');
      this.showScreen('lenses');
      return;
    }

    this.showScreen('method');
  }

  // ── Screen 2: Method ──

  onMethodClick(opt) {
    this._highlightOption('[data-screen="method"]', opt);
    this._data.method = opt.dataset.method;

    if (opt.dataset.method === 'upload') {
      this.showScreen('upload');
    } else if (opt.dataset.method === 'manual') {
      this.showScreen('manual');
    } else if (opt.dataset.method === 'email') {
      this._setLensesBack('method');
      this.showScreen('lenses');
    }
  }

  // ── Screen 3: Upload ──

  onFileChange(e) {
    const file = e.target.files[0];
    const screen = e.target.closest('.optical-prescription-picker__screen');
    const errorEl = screen.querySelector(
      '.optical-prescription-picker__file-error'
    );
    const labelText = screen.querySelector(
      '.optical-prescription-picker__file-label-text'
    );

    if (file) {
      this._data.file = file;
      this._data.fileInput = e.target;
      if (labelText) labelText.textContent = file.name;
      if (errorEl) errorEl.hidden = true;
      // show success icon
      const success = e.target.closest('.optical-prescription-picker__file-upload')?.querySelector('.optical-prescription-picker__file-success');
      if (success) success.hidden = false;
      // sync other inputs if needed
      this._syncFileSuccessIcons();
    } else {
      this._data.file = null;
      this._data.fileInput = null;
      const success = e.target.closest('.optical-prescription-picker__file-upload')?.querySelector('.optical-prescription-picker__file-success');
      if (success) success.hidden = true;
      this._syncFileSuccessIcons();
    }
  }

  onPdTypeChange(radio) {
    const screen = radio.closest('.optical-prescription-picker__screen');
    const single = screen.querySelector('[data-pd="single"]');
    const dual = screen.querySelector('[data-pd="dual"]');
    if (single) single.hidden = radio.value !== 'single';
    if (dual) dual.hidden = radio.value !== 'dual';
  }

  onSaveAndContinue() {
    const screen = this.querySelector('[data-screen="upload"]');

    if (!this._data.file) {
      const errorEl = screen.querySelector(
        '.optical-prescription-picker__file-error'
      );
      if (errorEl) errorEl.hidden = false;
      return;
    }

    // Collect PD
    this._collectPd(screen);

    this._setLensesBack('upload');
    this.showScreen('lenses');
  }

  // ── Screen 3b: Manual Prescription ──

  onManualSave() {
    const screen = this.querySelector('[data-screen="manual"]');

    // Collect prescription values
    const odSph = screen.querySelector('[data-rx="od-sph"]').value;
    const odCyl = screen.querySelector('[data-rx="od-cyl"]').value;
    const odAxis = screen.querySelector('[data-rx="od-axis"]').value;
    const osSph = screen.querySelector('[data-rx="os-sph"]').value;
    const osCyl = screen.querySelector('[data-rx="os-cyl"]').value;
    const osAxis = screen.querySelector('[data-rx="os-axis"]').value;

    if (odSph || odCyl || odAxis) {
      this._data.odRx =
        'SPH:' +
        (odSph || '-') +
        ' CYL:' +
        (odCyl || '-') +
        ' AXIS:' +
        (odAxis || '-');
    }
    if (osSph || osCyl || osAxis) {
      this._data.osRx =
        'SPH:' +
        (osSph || '-') +
        ' CYL:' +
        (osCyl || '-') +
        ' AXIS:' +
        (osAxis || '-');
    }

    // Collect PD (required)
    const pdType = screen.querySelector(
      'input[name^="optical-pd-type"]:checked'
    );
    if (pdType) {
      if (pdType.value === 'single') {
        const val = screen.querySelector('[data-pd-field="value"]').value;
        if (!val) {
          const err = screen.querySelector('[data-manual-pd-error]');
          if (err) err.hidden = false;
          return;
        }
        this._data.pd = 'Single PD | ' + val;
      } else {
        const right = screen.querySelector('[data-pd-field="right"]').value;
        const left = screen.querySelector('[data-pd-field="left"]').value;
        if (!right && !left) {
          const err = screen.querySelector('[data-manual-pd-error]');
          if (err) err.hidden = false;
          return;
        }
        this._data.pd =
          'Dual PD | ' + (right || '-') + ' | ' + (left || '-');
      }
    }

    // Hide PD error if we got past validation
    const err = screen.querySelector('[data-manual-pd-error]');
    if (err) err.hidden = true;

    this._setLensesBack('manual');
    this.showScreen('lenses');
  }

  // ── Screen 3a: Reading (save) ──
  onReadingSave() {
    const screen = this.querySelector('[data-screen="reading"]');
    if (!screen) return;

    const diopter = screen.querySelector('[data-reading="diopter"]')?.value;
    const diopterErr = screen.querySelector('[data-reading-diopter-error]');
    const pdErr = screen.querySelector('[data-reading-pd-error]');

    let ok = true;
    if (!diopter) {
      if (diopterErr) diopterErr.hidden = false;
      ok = false;
    } else if (diopterErr) diopterErr.hidden = true;

    // Collect PD from the reading screen — but validate first
    const pdType = screen.querySelector('input[name^="optical-pd-type-reading-"]') || screen.querySelector('input[name^="optical-pd-type"]');
    if (pdType) {
      if (pdType.checked && pdType.value === 'single') {
        const val = screen.querySelector('[data-pd-field="value"]')?.value;
        if (!val) {
          if (pdErr) pdErr.hidden = false;
          ok = false;
        } else if (pdErr) pdErr.hidden = true;
      } else if (pdType.checked && pdType.value === 'dual') {
        const right = screen.querySelector('[data-pd-field="right"]')?.value;
        const left = screen.querySelector('[data-pd-field="left"]')?.value;
        if (!right && !left) {
          if (pdErr) pdErr.hidden = false;
          ok = false;
        } else if (pdErr) pdErr.hidden = true;
      }
    }

    if (!ok) return;

    this._data.readingDiopter = diopter;

    // Collect PD into _data
    this._collectPd(screen);

    this._setLensesBack('reading');
    this.showScreen('lenses');
  }

  // ── Screen 4: Lens Selection ──

  onLensClick(card) {
    // Deselect all visible lens cards
    const container = this.querySelector(
      '.optical-prescription-picker__lens-groups:not([hidden])'
    );
    if (container) {
      container
        .querySelectorAll('.optical-prescription-picker__lens-card')
        .forEach((c) =>
          c.classList.remove('optical-prescription-picker__lens-card--selected')
        );
    }
    card.classList.add('optical-prescription-picker__lens-card--selected');

    this._data.lensVariantId = card.dataset.variantId;
    this._data.lensTitle = card.dataset.lensTitle;
    this._data.lensPrice = card.dataset.lensPrice;
    // Normalize lens group early so review and other places show a simplified label
    const rawLensGroup = card.dataset.lensGroup || '';
    this._data.lensGroupRaw = rawLensGroup;
    const rawLower = rawLensGroup.toLowerCase();
    this._data.lensGroup = rawLower.includes('blue')
      ? (this.dataset.translateGroupBlue || 'Blue-Light-Blocking Lenses')
      : (this.dataset.translateGroupClear || 'Clear Lenses');

    // Auto-advance to sun lenses after brief delay
    setTimeout(() => this.showScreen('sun-lenses'), 300);
  }

  // ── Screen 5: Sun Lenses ──

  onSunLensesClick(add) {
    this._data.addSunLenses = add;
    this._populateReview();
    this.showScreen('review');
  }

  // ── Screen 6: Review ──

  _populateReview() {
    const set = (sel, text) => {
      const el = this.querySelector('[data-review="' + sel + '"]');
      if (el) el.textContent = text;
    };

    // Frame
    set('frame-title', this.dataset.productTitle);
    set(
      'frame-price',
      this.dataset.productPrice
    );
    set(
      'frame-color',
      'Color: ' + this.dataset.productColor
    );

    // Lens
    set('lens-title', this._data.lensTitle);
    set('lens-price', this._data.lensPrice);
    set(
      'lens-type',
      'Prescription Type: ' + this._data.type
    );
    // Show a simplified lens group label: either Clear Lenses or Blue-Light-Blocking
    const rawGroup = (this._data.lensGroup || '').toLowerCase();
    const groupText = rawGroup.includes('blue')
      ? this.dataset.translateGroupBlue || 'Blue-Light-Blocking Lenses'
      : this.dataset.translateGroupClear || 'Clear Lenses';
    set('lens-group', 'Lens Group: ' + groupText);
    set('lens-frame', 'Frame: ' + this.dataset.productTitle);

    // PD (if present)
    if (this._data.pd) {
      set('lens-pd', 'PD: ' + this._data.pd);
    } else {
      set('lens-pd', '');
    }

    // Diopters / prescription display
    if (this._data.readingDiopter) {
      // Reading flow: same diopter for both eyes
      set(
        'lens-diopters',
        'Diopters: ' + this._data.readingDiopter + ' | ' + this._data.readingDiopter + ' | single'
      );
    } else if (this._data.odRx || this._data.osRx) {
      // Manual entry: show OD / OS strings
      set('lens-diopters', 'Diopters: ' + (this._data.odRx || '-') + ' | ' + (this._data.osRx || '-'));
    } else {
      set('lens-diopters', '');
    }

    // File preview button (show if user uploaded a file)
    const fileArea = this.querySelector('[data-review="file-area"]');
    if (fileArea) {
      const previewBtn = fileArea.querySelector('[data-action="preview-file"]');
      if (this._data.file && previewBtn) {
        fileArea.hidden = false;
        // attach click handler (ensure single binding)
        if (!previewBtn._bound) {
          previewBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this._openFilePreview();
          });
          previewBtn._bound = true;
        }
      } else if (fileArea) {
        fileArea.hidden = true;
      }
    }

    // Add-ons
    const addonsSection = this.querySelector('[data-review="addons-section"]');
    const addonsDividerTop = this.querySelector(
      '[data-review="addons-divider-top"]'
    );

    if (this._data.addSunLenses) {
      set('addon-title', this.dataset.sunLensesTitle);
      set('addon-price', this.dataset.sunLensesPrice);
      if (addonsSection) addonsSection.hidden = false;
      if (addonsDividerTop) addonsDividerTop.hidden = false;
    } else {
      if (addonsSection) addonsSection.hidden = true;
      if (addonsDividerTop) addonsDividerTop.hidden = true;
    }
  }

  async onAddToCart() {
    const addBtn = this.querySelector(
      '.optical-prescription-picker__add-to-cart'
    );
    if (addBtn) {
      addBtn.disabled = true;
      addBtn.classList.add('loading');
      const spinner = addBtn.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    try {
      const notes = this.querySelector(
        '.optical-prescription-picker__order-notes'
      );
      const orderNotes = notes ? notes.value.trim() : '';

      // Build prescription properties
      const lensProps = {};
      const frameProps = {};

      if (this._data.type) {
        lensProps['_Prescription Type'] = this._data.type;
        frameProps['_Prescription Type'] = this._data.type;
      }

      if (this._data.lensGroup) {
        lensProps['_Lens Group'] = this._data.lensGroup;
      }

      if (this._data.method) {
        lensProps['_Prescription Method'] = this._data.method;
      }

      if (this._data.odRx) {
        lensProps['_OD (Right Eye)'] = this._data.odRx;
      }
      if (this._data.osRx) {
        lensProps['_OS (Left Eye)'] = this._data.osRx;
      }

      if (this._data.readingDiopter) {
        lensProps['_Diopters'] = this._data.readingDiopter;
      }

      if (this._data.pd) {
        lensProps['_PD'] = this._data.pd;
      }

      // Add in reverse order: newest item appears first in cart drawer
      if (this._data.addSunLenses && this.dataset.sunLensesVariantId) {
        await this._ajaxAdd(this.dataset.sunLensesVariantId, 1);
      }

      if (this._data.lensVariantId) {
        if (this._data.file) {
          // File upload requires multipart/form-data POST (not supported by /cart/add.js)
          await this._multipartAdd(this._data.lensVariantId, lensProps, this._data.file);
        } else {
          await this._ajaxAdd(this._data.lensVariantId, 1, lensProps);
        }
      }

      await this._ajaxAdd(this.dataset.variantId, 1, frameProps);

      // Set order notes as cart note
      if (orderNotes) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: orderNotes }),
        });
      }

      // Open cart drawer — same pattern as ProductForm.onSubmitHandler:
      // use FormData with sections so the response includes rendered
      // cart drawer HTML, then pass it to cart.renderContents().
      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');
      if (cart) {
        if (typeof publish !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'optical-prescription-picker',
          });
        }
        const sectionsParam = cart
          .getSectionsToRender()
          .map((s) => s.id)
          .join(',');
        const sectionsResp = await fetch(
          window.location.pathname + '?sections=' + sectionsParam
        );
        const sections = await sectionsResp.json();
        cart.renderContents({ sections: sections });
      } else {
        window.location = window.routes.cart_url;
      }

      this.close();
    } catch (e) {
      console.error('Add to cart error:', e);
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.classList.remove('loading');
        const spinner = addBtn.querySelector('.loading-overlay__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
    }
  }

  async _ajaxAdd(variantId, quantity, properties) {
    const body = {
      id: parseInt(variantId, 10),
      quantity: quantity,
    };

    if (properties && Object.keys(properties).length > 0) {
      body.properties = properties;
    }

    const resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const data = await resp.json();
    if (data.status) throw new Error(data.description);
    return data;
  }

  _multipartAdd(variantId, properties, file) {
    return new Promise((resolve, reject) => {
      const form = this.querySelector('.optical-prescription-picker__file-form');
      const iframe = form?.previousElementSibling;
      if (!form || !iframe) {
        reject(new Error('File upload form not found'));
        return;
      }

      // Set variant ID
      form.querySelector('input[name="id"]').value = variantId;

      // Remove any previous dynamic inputs
      form.querySelectorAll('.dynamic-prop').forEach((el) => el.remove());

      // Add properties as hidden inputs
      if (properties) {
        for (const [key, value] of Object.entries(properties)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[' + key + ']';
          input.value = value;
          input.className = 'dynamic-prop';
          form.appendChild(input);
        }
      }

      // Clone the file input so the original stays in the UI
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.name = 'properties[_Prescription File]';
      fileInput.className = 'dynamic-prop';
      fileInput.style.display = 'none';

      // Transfer the file via DataTransfer
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInput.files = dt.files;
      form.appendChild(fileInput);

      // Listen for iframe load (signals form submission complete)
      const onLoad = () => {
        iframe.removeEventListener('load', onLoad);
        resolve();
      };
      iframe.addEventListener('load', onLoad);

      // Submit the multipart form to the hidden iframe
      form.submit();
    });
  }

  // ── Frame Only ──

  _clickSubmitButton() {
    const form = document.querySelector(
      '#product-form-' + this._sectionId
    );
    if (!form) return;
    const btn = form.querySelector('[name="add"]');
    if (btn) {
      btn.disabled = false;
      btn.click();
    }
  }

  // ── Helpers ──

  _collectPd(screen) {
    const pdType = screen.querySelector(
      'input[name^="optical-pd-type"]:checked'
    );
    if (pdType) {
      if (pdType.value === 'single') {
        const val = screen.querySelector('[data-pd-field="value"]').value;
        if (val) this._data.pd = 'Single PD | ' + val;
      } else {
        const right = screen.querySelector('[data-pd-field="right"]').value;
        const left = screen.querySelector('[data-pd-field="left"]').value;
        if (right || left)
          this._data.pd =
            'Dual PD | ' + (right || '-') + ' | ' + (left || '-');
      }
    }
  }

  // Ensure file success icons reflect current input state
  _syncFileSuccessIcons() {
    this.querySelectorAll('.optical-prescription-picker__file-input').forEach((input) => {
      const uploadContainer = input.closest('.optical-prescription-picker__file-upload');
      const success = uploadContainer?.querySelector('.optical-prescription-picker__file-success');
      const hasFile = (input.files && input.files.length > 0) || (this._data && this._data.file && this._data.fileInput === input);
      if (success) success.hidden = !hasFile;
    });
  }

  _clearFile() {
    this._data.file = null;
    this._data.fileInput = null;

    this.querySelectorAll('.optical-prescription-picker__file-input').forEach(
      (input) => {
        input.value = '';
        input.removeAttribute('name');
        // Move back to its upload container if it was moved to the cart form
        const container = input.closest(
          '.optical-prescription-picker__file-upload'
        );
        if (!container) {
          const originScreen = input.dataset.originScreen || 'upload';
          const screen = this.querySelector(
            '[data-screen="' + originScreen + '"]'
          );
          const uploadDiv = screen?.querySelector(
            '.optical-prescription-picker__file-upload'
          );
          if (uploadDiv) {
            uploadDiv.insertBefore(
              input,
              uploadDiv.querySelector(
                '.optical-prescription-picker__file-label'
              )
            );
          }
        }
      }
    );

    this.querySelectorAll(
      '.optical-prescription-picker__file-label-text'
    ).forEach((label) => {
      label.textContent =
        label.closest('.optical-prescription-picker__file-label')?.dataset
          ?.defaultText || 'Choose File';
    });

    // hide success icons
    this.querySelectorAll('.optical-prescription-picker__file-success').forEach((el)=> el.hidden = true);

    this.querySelectorAll(
      '.optical-prescription-picker__file-error'
    ).forEach((el) => {
      el.hidden = true;
    });
  }

  _setLensesBack(screen) {
    const btn = this.querySelector(
      '[data-screen="lenses"] .optical-prescription-picker__back'
    );
    if (btn) btn.dataset.back = screen;
  }

  _highlightOption(screenSelector, opt) {
    this.querySelectorAll(
      screenSelector + ' .optical-prescription-picker__option'
    ).forEach((o) =>
      o.classList.remove('optical-prescription-picker__option--selected')
    );
    opt.classList.add('optical-prescription-picker__option--selected');
  }

  // Clear prescription-specific UI/state when user changes prescription type
  _clearPrescriptionState() {
    // Clear stored prescription data
    delete this._data.pd;
    delete this._data.odRx;
    delete this._data.osRx;
    delete this._data.readingDiopter;
    // Clear file state
    this._clearFile();
    // Clear lens selection
    delete this._data.lensVariantId;
    delete this._data.lensTitle;
    delete this._data.lensPrice;
    delete this._data.lensGroup;
    delete this._data.lensGroupRaw;
    this.querySelectorAll('.optical-prescription-picker__lens-card').forEach((c)=>c.classList.remove('optical-prescription-picker__lens-card--selected'));

    // Reset PD selects and radio states
    this.querySelectorAll('input[type="radio"][name^="optical-pd-type"]').forEach((r)=>{ r.checked = r.value === 'single'; });
    this.querySelectorAll('[data-pd="single"]').forEach((el)=> el.hidden = false);
    this.querySelectorAll('[data-pd="dual"]').forEach((el)=> el.hidden = true);
    this.querySelectorAll('.optical-prescription-picker__pd-select').forEach((sel)=> sel.selectedIndex = 0);

    // Reset manual prescription selects and re-disable AXIS
    this.querySelectorAll('[data-rx]').forEach((sel) => {
      sel.selectedIndex = 0;
      if (sel.dataset.rx && sel.dataset.rx.endsWith('-axis')) sel.disabled = true;
    });

    // Reset reading selects
    this.querySelectorAll('[data-reading]').forEach((sel)=> sel.selectedIndex = 0);

    // Hide any PD/diopter error messages
    this.querySelectorAll('[data-manual-pd-error], [data-reading-pd-error], [data-reading-diopter-error]').forEach((el)=> el.hidden = true);

    // Close preview modal if open
    this._closeFilePreview();
  }

  // Open file preview modal
  _openFilePreview() {
    const modal = this.querySelector('.optical-prescription-picker__file-modal');
    const img = modal?.querySelector('[data-file-preview-img]');
    if (!modal || !img || !this._data.file) return;

    // Revoke previous URL if any
    if (this._currentPreviewUrl) {
      URL.revokeObjectURL(this._currentPreviewUrl);
      this._currentPreviewUrl = null;
    }

    try {
      this._currentPreviewUrl = URL.createObjectURL(this._data.file);
      img.src = this._currentPreviewUrl;
    } catch (e) {
      console.error('Preview URL error', e);
      return;
    }

    // Ensure modal is attached to body so it overlays everything
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    modal.hidden = false;

    // Close handlers
    const closeBtn = modal.querySelector('[data-action="file-modal-close"]');
    const overlay = modal.querySelector('[data-action="file-modal-overlay"]');
    const closeHandler = () => this._closeFilePreview();
    closeBtn?.addEventListener('click', closeHandler);
    overlay?.addEventListener('click', closeHandler);

    // store handlers for removal
    modal._closeHandler = closeHandler;
  }

  _closeFilePreview() {
    // modal may have been moved to document.body; try both
    let modal = this.querySelector('.optical-prescription-picker__file-modal');
    let moved = false;
    if (!modal) {
      modal = document.querySelector('.optical-prescription-picker__file-modal');
      moved = true;
    }
    if (!modal) return;

    const img = modal.querySelector('[data-file-preview-img]');
    const closeBtn = modal.querySelector('[data-action="file-modal-close"]');
    const overlay = modal.querySelector('[data-action="file-modal-overlay"]');

    modal.hidden = true;
    if (img) img.src = '';
    if (this._currentPreviewUrl) {
      URL.revokeObjectURL(this._currentPreviewUrl);
      this._currentPreviewUrl = null;
    }

    if (modal._closeHandler) {
      closeBtn?.removeEventListener('click', modal._closeHandler);
      overlay?.removeEventListener('click', modal._closeHandler);
      delete modal._closeHandler;
    }

    // Move modal back into component so subsequent opens can find it via this.querySelector
    try {
      if (moved && this && this.appendChild) this.appendChild(modal);
    } catch (e) {
      // ignore
    }
  }

  _reset() {
    this._data = {};

    this.querySelectorAll('.optical-prescription-picker__option').forEach(
      (o) =>
        o.classList.remove('optical-prescription-picker__option--selected')
    );

    this.querySelectorAll('.optical-prescription-picker__lens-card').forEach(
      (c) =>
        c.classList.remove('optical-prescription-picker__lens-card--selected')
    );

    this._clearFile();

    // Reset all PD radios to single and all selects to first option
    this.querySelectorAll(
      'input[type="radio"][name^="optical-pd-type"][value="single"]'
    ).forEach((r) => (r.checked = true));
    this.querySelectorAll('[data-pd="single"]').forEach(
      (el) => (el.hidden = false)
    );
    this.querySelectorAll('[data-pd="dual"]').forEach(
      (el) => (el.hidden = true)
    );
    this.querySelectorAll('.optical-prescription-picker__pd-select').forEach(
      (sel) => (sel.selectedIndex = 0)
    );

    // Reset prescription selects and re-disable AXIS
    this.querySelectorAll('[data-rx]').forEach((sel) => {
      sel.selectedIndex = 0;
      if (sel.dataset.rx.endsWith('-axis')) sel.disabled = true;
    });

    // Reset reading screen selects
    this.querySelectorAll('[data-reading]').forEach((sel) => (sel.selectedIndex = 0));

    // Reset PD error
    this.querySelectorAll('[data-manual-pd-error]').forEach(
      (el) => (el.hidden = true)
    );

    const notes = this.querySelector(
      '.optical-prescription-picker__order-notes'
    );
    if (notes) notes.value = '';

    const addBtn = this.querySelector(
      '.optical-prescription-picker__add-to-cart'
    );
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.classList.remove('loading');
      const spinner = addBtn.querySelector('.loading-overlay__spinner');
      if (spinner) spinner.classList.add('hidden');
    }

    // Reset addons visibility
    const addonsSection = this.querySelector('[data-review="addons-section"]');
    if (addonsSection) addonsSection.hidden = true;
    const addonsDividerTop = this.querySelector('[data-review="addons-divider-top"]');
    if (addonsDividerTop) addonsDividerTop.hidden = true;

    this.showScreen('type');
  }
}

customElements.define('optical-prescription-picker', OpticalPrescriptionPicker);
